import type { CurrentIp, User } from '@repo-level/core-front';
import type { CmsABTest } from 'api/cms/abTests';
import { groupMember } from 'helpers/user';
import type { Locale } from 'languages';
import { sendOtherABTestAssignmentEvent } from 'modules/googleAnalytics/events/otherEvents';
import type { Country } from './conf';
import { LocalStorage } from './localStorage';

export const getRandomNumber = (from: number, to: number) =>
  Number((Math.random() * (to - from) + from).toFixed(3));

export type ABTestingVersion<T> = {
  name: string;
  probability?: number;
  component?: React.ComponentType<T> | null;
  isVersionToApply?: (user: User) => boolean;
};

type ABTestingConfig<T> = {
  authorized?: boolean;
  stags?: string[];
  countries?: Country[];
  locale?: Locale[];
  userGroup?: string;
  defaultVersion: React.ComponentType<T> | string | null;
  versions: ABTestingVersion<T>[];
  turnOffTest?: boolean;
};

const StagStore = new LocalStorage<string, null>('stag', null);

const ABTestingStore = (experiment: string) =>
  new LocalStorage<{ name: string }, null>(`ab-testing__${experiment}`, null);

type ABTestingVersionRequired<T> = Omit<ABTestingVersion<T>, 'probability'> & {
  probability: number;
};

const getRandomVersion = <T,>(versions: ABTestingVersionRequired<T>[]) => {
  const randomNumber = getRandomNumber(1, 100);

  const ascendingProbability = (
    versionA: ABTestingVersionRequired<T>,
    versionB: ABTestingVersionRequired<T>
  ) => versionA.probability - versionB.probability;

  const transformProbability = (
    version: ABTestingVersionRequired<T>,
    index: number,
    versions: ABTestingVersionRequired<T>[]
  ) => {
    if (index === 0) return version;

    const probability = versions
      .slice(0, index + 1)
      .reduce((sum, { probability }) => sum + probability, 0);

    return {
      ...version,
      probability: versions.length === index + 1 ? 100 : probability,
    };
  };

  const appropriateByRandomNumber = ({
    probability,
  }: ABTestingVersionRequired<T>) => probability >= randomNumber;

  return versions
    .sort(ascendingProbability)
    .map(transformProbability)
    .find(appropriateByRandomNumber);
};

export const abTestingFunction = <T extends object>(
  experiment: string,
  config: ABTestingConfig<T>,
  user: User | undefined,
  currentIp: CurrentIp | undefined,
  cmsABTests: CmsABTest[]
) => {
  if (config?.turnOffTest) return null;

  const cmsABTestVersion = cmsABTests?.find(
    ({ title }) => title === experiment
  )?.version;

  const configVersions = config.versions.map(({ name }) => name);

  if (cmsABTestVersion && configVersions.includes(cmsABTestVersion))
    return cmsABTestVersion;

  const ABTestingExperimentStore = ABTestingStore(experiment);

  const savedABTesting = ABTestingExperimentStore.get();

  const savedExperiment = config.versions.find(
    ({ name }) => name === savedABTesting?.name
  );

  if (savedExperiment != null)
    return savedExperiment.component !== undefined
      ? savedExperiment.component
      : savedExperiment.name;

  const byUserGroup = (() => {
    if (config.userGroup == null) return true;

    if (user?.statuses == null) return false;

    return groupMember(config.userGroup)(user);
  })();

  const byAuthorized = (() => {
    if (config.authorized == null) return true;

    if (config.authorized && user?.id != null) return true;

    if (!config.authorized && user?.id == null) return true;

    return false;
  })();

  const byStag = (() => {
    if (config.stags == null) return true;

    const storageStag = StagStore.get();

    if (storageStag == null) return false;

    return config.stags.includes(storageStag);
  })();

  const byCountry = (() => {
    if (config.countries == null) return true;

    return config.countries.includes(currentIp?.countryCode as Country);
  })();

  const conditions = byAuthorized && byUserGroup && byStag && byCountry;

  if (conditions) {
    const version = (() => {
      const configVersion = config.versions.find(
        ({ isVersionToApply }) => isVersionToApply && isVersionToApply(user!)
      );
      if (configVersion) return configVersion;

      const empty = config.versions.find(
        ({ probability }) => probability === undefined
      );
      if (empty) return;

      return getRandomVersion(config.versions as ABTestingVersionRequired<T>[]);
    })();

    if (version == null) return config.defaultVersion;

    ABTestingExperimentStore.replace({ name: version.name });

    const parts = version.name.split(':');

    sendOtherABTestAssignmentEvent({
      test_name: experiment,
      test_group: version.name,
      test_version: parts[parts.length - 1] as string,
    });

    return version.component !== undefined ? version.component : version.name;
  }

  return config.defaultVersion;
};
